import { useEffect, useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { getAuthSnapshot } from '@/context/auth/deriveAuth';
import {
  getSessionEndMessage,
  isSessionEnding,
  SESSION_END_REASON_KEY,
  type TSessionEndReason,
} from '@/services/endSession';
import {
  Button,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  Link as ChakraLink,
  Stack,
  Text,
  Image,
  VStack,
  HStack,
  Box,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuthContext } from '@/context/auth/useAuthContext';
import appMessages from '@/messages';
import authMessages from '@/messages/auth';
import iconsPng from '@/assets/img/png/icons';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import authRoutes from '@/constants/routes/auth';
import CustomButton from '@/components/atoms/CustomButton';
import BackgroundImagePage from '@/components/templates/BackgroundImagePage';
import { normalizeAuthRedirectLink } from '@/utils/authRedirectLink';

type FormData = {
  email: string;
  password: string;
};

// NOTE: If we expect some custom validation rules, consider moving yup instance to separate file and import from there
const schema = yup
  .object({
    email: yup.string().required('Email is required.').email('Please enter valid email address.'),
    password: yup.string().required('Password is required.'),
  })
  .required();

const SignIn = () => {
  const { link } = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate({ from: '/sign-in' });
  const { signIn } = useAuthContext();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'all',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [customErrorMessage, setCustomErrorMessage] = useState(() => {
    try {
      const reason = sessionStorage.getItem(SESSION_END_REASON_KEY) as TSessionEndReason | null;
      return getSessionEndMessage(reason);
    } catch {
      return '';
    }
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_END_REASON_KEY);
    } catch {
      // Sign-in remains usable when sessionStorage is unavailable.
    }
  }, []);

  // Clear a failed-sign-in message as soon as the user edits either field. Without
  // this, "wrong password" sat under a corrected, valid form until the next
  // submit, reading as though the correction had already been rejected.
  const clearCustomError = () => setCustomErrorMessage((message) => (message ? '' : message));

  const { mutateAsync: handleSignIn, isPending: isHandleSignInPending } = useMutation({
    mutationFn: async (data: FormData) => {
      setCustomErrorMessage('');

      try {
        await signIn(data.email, data.password);
      } catch (err: any) {
        setCustomErrorMessage(err.errors?.[0]?.message || err.message);
        return;
      }

      // One explicit navigation, and no router.invalidate(). Navigating runs the
      // destination's beforeLoad fresh, and those guards now read the store, so
      // invalidate buys nothing — it would re-run *this* route's guard, which now
      // correctly sees an authenticated user and throws its own redirect, and this
      // navigation would then fire a second one on top of it.
      //
      // '/' is the fan-out: _authenticated/index routes member-care users on to
      // their own area, so this is "go home", not "go to the admin users table".
      const target = normalizeAuthRedirectLink(link);

      if (target) {
        // navigate({ href }) is not typed in router 1.58 — NavigateOptions extends
        // ToOptions, which declares `to`, not `href`. `to` would not do either: it
        // is a path target, so a link carrying a query string would have it
        // swallowed. history.replace takes exactly what normalizeAuthRedirectLink
        // returns (pathname + search + hash), and the router follows history.
        router.history.replace(target);
        return;
      }

      await navigate({ to: '/', replace: true });
    },
  });

  return (
    <BackgroundImagePage heroChildrenTopMargin={0}>
      <VStack justifyContent={'center'} alignItems={'center'} maxHeight={'100vh'} height={'100vh'}>
        <Text variant={'loraTitle'} position={'relative'} color={'white'} maxWidth={'400px'}>
          {authMessages.signIn}
        </Text>
        <VStack minW={{ md: FORM_FIELD_WIDTH }} marginTop={'10px'} alignItems={'center'}>
          <form onSubmit={handleSubmit((data) => handleSignIn(data))}>
            <Stack spacing={4} p="1rem">
              <FormControl isInvalid={!!errors.email}>
                <Input
                  className="custom-input"
                  {...register('email', { onChange: clearCustomError })}
                  placeholder={appMessages.email}
                  data-state={touchedFields.email ? (errors.email?.message ? 'invalid' : 'valid') : ''}
                />
                <Text color={'extra.red'}>{errors.email?.message ?? ''}</Text>
              </FormControl>
              <FormControl isInvalid={!!errors.password}>
                <InputGroup>
                  <Input
                    className="custom-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={appMessages.password}
                    {...register('password', { onChange: clearCustomError })}
                    data-state={touchedFields.password ? (errors.password?.message ? 'invalid' : 'valid') : ''}
                  />
                  <InputRightElement width="4rem" height={'100%'}>
                    <Button onClick={() => setShowPassword(!showPassword)} backgroundColor={'transparent'}>
                      <Image
                        src={showPassword ? iconsPng.eyeClosed : iconsPng.eyeOpened}
                        width={25}
                        height={25}
                        alt={showPassword ? 'eyeOpened' : 'eyeClosed'}
                      />
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <Text color={'extra.red'}>{errors.password?.message ?? ''}</Text>
              </FormControl>
              <Box position={'relative'}>
                <Text
                  color={'secondary.500'}
                  fontWeight={'900'}
                  fontSize={'16px'}
                  position={'absolute'}
                  top={'-2'}
                  width={'100%'}
                  textAlign={'left'}
                  paddingX={'16px'}
                >
                  {customErrorMessage}
                </Text>
              </Box>
              <CustomButton
                label={authMessages.signIn}
                buttonType="submit"
                backgroundColor="secondary.500"
                width="300px"
                isLoading={isHandleSignInPending}
                disabled={!isValid}
                style={{ marginTop: '40px' }}
              />
            </Stack>
          </form>
          <HStack width={'100%'} justifyContent={'center'}>
            <ChakraLink
              as={Link}
              to={authRoutes.forgotPassword}
              color="text.veryLightBlue"
              fontSize={'15px'}
              fontWeight={'600'}
              lineHeight={'20px'}
              textAlign={'center'}
            >
              {authMessages.forgotPassword}
            </ChakraLink>
          </HStack>
        </VStack>
      </VStack>
    </BackgroundImagePage>
  );
};

export const Route = createFileRoute('/sign-in/')({
  component: SignIn,
  validateSearch: (search: Record<string, unknown>) => {
    const link = normalizeAuthRedirectLink(search.link);
    if (link) {
      return {
        link,
      };
    }

    delete search.link;
    return {};
  },
  beforeLoad: async ({ search }) => {
    // A teardown navigates here *before* clearing the store, so the user is still
    // "authenticated" when this guard runs. Without this the redirect below would
    // bounce them straight back into the app they are signing out of.
    if (isSessionEnding()) return;

    const { isAuthenticated } = getAuthSnapshot();

    if (!isAuthenticated) return;

    // An already-signed-in visitor landing on /sign-in. Honour the deep link here
    // rather than forwarding it to '/': nothing downstream consumes the param any
    // more, so passing it on would leave ?link=... in the address bar doing
    // nothing. redirect() does type `href`, unlike navigate().
    const target = normalizeAuthRedirectLink(search.link);

    throw target ? redirect({ href: target }) : redirect({ to: '/' });
  },
});
